import {
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  File,
  FileCode,
  FileText,
  Folder,
  FolderDown,
  FolderOpen,
  FolderPlus,
  FolderUp,
  Image as ImageIcon,
  PanelLeftClose,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import { useRef, useState } from "react";
import { api } from "@/api/client";
import { useDeleteFile, useFiles, useMkdir } from "@/api/hooks";
import type { FileEntry } from "@/api/types";

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "svg", "webp"]);

/**
 * One file scheduled for upload, optionally with its directory path
 * relative to whatever folder the user dropped/picked. The orchestrator
 * creates intermediate dirs automatically (mkdir=true on Sprites FS write),
 * so we don't need to issue any pre-mkdir calls — we just upload each file
 * with its full target dir.
 */
interface UploadItem {
  file: File;
  relativeDir: string;
}

/**
 * Walk a `DataTransferItem` as a directory, depth-first, to collect all
 * leaf files with their relative paths. Used for drag-and-drop folder
 * uploads — `dataTransfer.files` flattens to the dropped items only and
 * loses subfolder structure, so we have to use the `webkitGetAsEntry`
 * tree walker. Falls back to `null` if the browser doesn't expose entries
 * (callers then use the FileList path).
 *
 * Browser support: Chromium, Safari, Firefox all expose this on drops; it
 * is non-standard but de-facto interoperable. The async readEntries chunks
 * at ~100 entries per call, hence the loop.
 */
async function readEntryRecursive(
  entry: FileSystemEntry,
  parentDir: string,
): Promise<UploadItem[]> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await new Promise<File>((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
    return [{ file, relativeDir: parentDir }];
  }
  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    const items: UploadItem[] = [];
    const childDir = parentDir ? `${parentDir}/${entry.name}` : entry.name;
    while (true) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      if (batch.length === 0) break;
      for (const child of batch) {
        items.push(...(await readEntryRecursive(child, childDir)));
      }
    }
    return items;
  }
  return [];
}

/**
 * Given a `DataTransfer` from a drop event, return all the files the user
 * dropped including those inside any dropped folders. Prefers the
 * `items[]` path (which gives us folder structure) and falls back to the
 * flat `files[]` list.
 */
async function collectDropItems(dt: DataTransfer): Promise<UploadItem[]> {
  if (dt.items && dt.items.length > 0) {
    const out: UploadItem[] = [];
    for (let i = 0; i < dt.items.length; i++) {
      const item = dt.items[i];
      if (!item || item.kind !== "file") continue;
      const entry = item.webkitGetAsEntry?.();
      if (entry) {
        out.push(...(await readEntryRecursive(entry, "")));
        continue;
      }
      const f = item.getAsFile?.();
      if (f) out.push({ file: f, relativeDir: "" });
    }
    return out;
  }
  return Array.from(dt.files ?? []).map((f) => ({
    file: f,
    relativeDir: "",
  }));
}

interface FilesPanelProps {
  deskId: string;
  relayUrl: string | null;
  onFileOpen: (path: string, name: string, root: "data" | "sprite") => void;
  selectedPath: string | null;
  onCollapse: () => void;
  autoRefresh: boolean;
}

export function FilesPanel({
  deskId,
  relayUrl,
  onFileOpen,
  selectedPath,
  onCollapse,
  autoRefresh,
}: FilesPanelProps) {
  const [currentPath, setCurrentPath] = useState("/");
  const [browseRoot, setBrowseRoot] = useState<"data" | "sprite">("data");
  const { data, isLoading, refetch, error } = useFiles(
    deskId,
    currentPath,
    autoRefresh,
    browseRoot,
  );
  const mkdir = useMkdir(deskId);
  const deleteFile = useDeleteFile(deskId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [mkdirName, setMkdirName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Batch upload progress: null = idle. We intentionally roll our own state
  // here instead of leaning on TanStack mutation pending counts because the
  // user wants to see "12 / 47" + a progress bar, not "queued: ?".
  //
  // bytesDone / bytesTotal drive the percentage bar (more honest than file
  // count when sizes are skewed — uploading 50 KB of CSS then a 200 MB
  // dataset shouldn't read as 50% after one file). currentName is whatever
  // file the most-recently-started worker just kicked off, so the row
  // updates ~once per concurrent worker startup.
  const [uploadProgress, setUploadProgress] = useState<{
    done: number;
    total: number;
    failed: number;
    bytesDone: number;
    bytesTotal: number;
    currentName: string | null;
  } | null>(null);

  const entries = data?.entries ?? [];
  const pathSegments =
    currentPath === "/" ? [] : currentPath.replace(/^\//, "").split("/");

  /**
   * Upload a batch of files (possibly with nested relative dirs) into the
   * current folder. Runs uploads with a small concurrency cap so the
   * orchestrator + sprite FS API don't get hammered with hundreds of
   * parallel writes; serial would be too slow on big folders, all-parallel
   * tends to time out a few in the middle.
   */
  const handleUploadItems = async (items: UploadItem[]) => {
    if (items.length === 0) return;
    const bytesTotal = items.reduce((acc, it) => acc + (it.file.size ?? 0), 0);
    setUploadProgress({
      done: 0,
      total: items.length,
      failed: 0,
      bytesDone: 0,
      bytesTotal,
      currentName: items[0]?.file.name ?? null,
    });
    const CONCURRENCY = 4;
    let cursor = 0;
    let done = 0;
    let failed = 0;
    let bytesDone = 0;
    const worker = async () => {
      while (true) {
        const i = cursor++;
        if (i >= items.length) return;
        const item = items[i]!;
        // Surface the just-started filename — gives the user a real "we're
        // working on it" signal even when one big file is sitting in the
        // middle of a small batch.
        setUploadProgress((prev) =>
          prev ? { ...prev, currentName: item.file.name } : prev,
        );
        try {
          await api.uploadFile(
            deskId,
            currentPath,
            item.file,
            item.relativeDir || undefined,
          );
        } catch {
          failed++;
        }
        done++;
        bytesDone += item.file.size ?? 0;
        setUploadProgress((prev) =>
          prev
            ? { ...prev, done, failed, bytesDone }
            : prev,
        );
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, items.length) }, () =>
        worker(),
      ),
    );
    refetch();
    // Hold the final state on screen briefly so the user sees "Done".
    setTimeout(() => setUploadProgress(null), 1800);
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{
        background: "var(--surface)",
        borderRight: "1px solid var(--blt)",
        minWidth: 0,
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        // Snapshot synchronously — DataTransfer is invalidated after the
        // event handler returns, so we can't await before reading items.
        const dt = e.dataTransfer;
        collectDropItems(dt).then((items) => {
          if (items.length > 0) void handleUploadItems(items);
        });
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-1 px-2 py-1.5 border-b"
        style={{ borderColor: "var(--blt)", flexShrink: 0 }}
      >
        <span
          className="text-[11px] font-medium uppercase tracking-wider flex-1"
          style={{
            fontFamily: "var(--font-mono)",
            color: "var(--t3)",
            letterSpacing: "0.08em",
          }}
        >
          Files
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="p-1 rounded hover:bg-[var(--hover)]"
          style={{ color: "var(--t3)" }}
          title="Refresh"
        >
          <RefreshCw size={12} />
        </button>
        <a
          href={api.downloadFolderUrl(deskId, currentPath, browseRoot)}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded hover:bg-[var(--hover)]"
          style={{ color: "var(--t3)" }}
          title="Download this folder as ZIP"
        >
          <FolderDown size={12} />
        </a>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="p-1 rounded hover:bg-[var(--hover)]"
          style={{ color: "var(--t3)" }}
          title="Upload files"
        >
          <Upload size={12} />
        </button>
        <button
          type="button"
          onClick={() => folderInputRef.current?.click()}
          className="p-1 rounded hover:bg-[var(--hover)]"
          style={{ color: "var(--t3)" }}
          title="Upload folder"
        >
          <FolderUp size={12} />
        </button>
        <button
          type="button"
          onClick={() => setMkdirName("")}
          className="p-1 rounded hover:bg-[var(--hover)]"
          style={{ color: "var(--t3)" }}
          title="New folder"
        >
          <FolderPlus size={12} />
        </button>
        <button
          type="button"
          onClick={onCollapse}
          className="p-1 rounded hover:bg-[var(--hover)]"
          style={{ color: "var(--t3)" }}
          title="Collapse files"
        >
          <PanelLeftClose size={12} />
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const list = e.target.files;
          if (!list) return;
          const items: UploadItem[] = Array.from(list).map((file) => ({
            file,
            relativeDir: "",
          }));
          void handleUploadItems(items);
          e.target.value = "";
        }}
      />

      {/*
        Folder picker. `webkitdirectory` is the de-facto standard attribute
        (Chrome/Edge/Safari/Firefox all honour it on `<input type=file>`),
        and the resulting File objects carry `webkitRelativePath` like
        `picked-folder/sub/file.txt`. We split off the basename so the
        browser-supplied `file.name` isn't doubled.
      */}
      <input
        ref={folderInputRef}
        type="file"
        // @ts-expect-error -- webkitdirectory isn't in React's HTMLInput typings, but every modern browser honours it
        webkitdirectory=""
        directory=""
        multiple
        className="hidden"
        onChange={(e) => {
          const list = e.target.files;
          if (!list) return;
          const items: UploadItem[] = Array.from(list).map((file) => {
            const rel = (file as File & { webkitRelativePath?: string })
              .webkitRelativePath;
            const segments = (rel ?? file.name).split("/");
            segments.pop();
            return { file, relativeDir: segments.join("/") };
          });
          void handleUploadItems(items);
          e.target.value = "";
        }}
      />

      {/* Breadcrumbs */}
      <div
        className="flex items-center gap-0.5 px-2 py-1 text-[11px] border-b overflow-x-auto hide-scrollbar"
        style={{
          borderColor: "var(--blt)",
          color: "var(--t3)",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={() => {
            setBrowseRoot(browseRoot === "data" ? "sprite" : "data");
            setCurrentPath("/");
          }}
          className="shrink-0 px-1 py-0.5 rounded text-[10px] font-medium"
          style={{
            fontFamily: "var(--font-mono)",
            background:
              browseRoot === "sprite"
                ? "color-mix(in srgb, var(--accent) 10%, transparent)"
                : "transparent",
            color: browseRoot === "sprite" ? "var(--accent)" : "var(--t2)",
          }}
          title={
            browseRoot === "data"
              ? "Browse full sprite filesystem"
              : "Back to workspace"
          }
        >
          {browseRoot === "sprite" ? "~/sprite" : "~/data"}
        </button>
        {pathSegments.map((seg, i) => {
          const segPath = `/${pathSegments.slice(0, i + 1).join("/")}`;
          const isLast = i === pathSegments.length - 1;
          return (
            <span key={segPath} className="flex items-center gap-0.5 shrink-0">
              <ChevronRight size={8} style={{ color: "var(--t4)" }} />
              {isLast ? (
                <span className="font-medium" style={{ color: "var(--t1)" }}>
                  {seg}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setCurrentPath(segPath)}
                  className="hover:underline"
                >
                  {seg}
                </button>
              )}
            </span>
          );
        })}
      </div>

      {/* mkdir inline */}
      {mkdirName !== null && (
        <div
          className="px-2 py-1 border-b"
          style={{ borderColor: "var(--blt)", flexShrink: 0 }}
        >
          <input
            type="text"
            value={mkdirName}
            onChange={(e) => setMkdirName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && mkdirName.trim()) {
                const base = currentPath === "/" ? "" : currentPath;
                mkdir.mutate(`${base}/${mkdirName.trim()}`);
                setMkdirName(null);
              }
              if (e.key === "Escape") setMkdirName(null);
            }}
            // biome-ignore lint/a11y/noAutofocus: focus is desired on transient input
            autoFocus
            placeholder="folder name..."
            className="w-full bg-[var(--bg)] border rounded px-2 py-1 text-xs focus:outline-none"
            style={{ borderColor: "var(--border)", color: "var(--t1)" }}
          />
        </div>
      )}

      {/* Tree */}
      <div
        className="flex-1 overflow-y-auto px-1 py-1 relative"
        style={{
          background: dragOver
            ? "color-mix(in srgb, var(--accent) 6%, transparent)"
            : undefined,
        }}
      >
        {isLoading && entries.length === 0 && <FilesSkeleton />}
        {error && (
          <div className="px-3 py-4 space-y-2">
            <p className="text-[12px]" style={{ color: "var(--t2)" }}>
              Couldn't list this folder
            </p>
            <p
              className="text-[11px]"
              style={{ color: "var(--t4)", fontFamily: "var(--font-mono)" }}
            >
              {error instanceof Error ? error.message.slice(0, 120) : ""}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-[11px] px-2 py-1 rounded"
              style={{
                background:
                  "color-mix(in srgb, var(--accent) 10%, transparent)",
                color: "var(--accent)",
                border:
                  "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
              }}
            >
              Try again
            </button>
          </div>
        )}
        {!isLoading && !error && entries.length === 0 && (
          <div className="px-3 py-8 text-center">
            <File
              size={20}
              className="mx-auto mb-2"
              style={{ color: "var(--t4)", opacity: 0.5 }}
            />
            <p className="text-[11px]" style={{ color: "var(--t4)" }}>
              Empty folder
            </p>
            <p
              className="text-[10px] mt-1"
              style={{ color: "var(--t4)", opacity: 0.7 }}
            >
              Drop files here or use the upload button
            </p>
          </div>
        )}
        {entries
          .slice()
          .sort((a, b) => {
            if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
            return a.name.localeCompare(b.name);
          })
          .map((entry) => (
            <FileRow
              key={entry.name}
              entry={entry}
              deskId={deskId}
              depth={0}
              onFileClick={onFileOpen}
              onDelete={(path) => deleteFile.mutate(path)}
              autoRefresh={autoRefresh}
              selectedPath={selectedPath}
              relayUrl={relayUrl}
              parentPath=""
              browseRoot={browseRoot}
            />
          ))}
      </div>

      {/* Drag overlay */}
      {dragOver && (
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center border-2 border-dashed m-2 rounded-lg z-10"
          style={{
            borderColor: "var(--accent)",
            background: "color-mix(in srgb, var(--accent) 6%, transparent)",
          }}
        >
          <div className="text-center px-4">
            <p
              className="text-[13px] font-medium"
              style={{ color: "var(--accent)" }}
            >
              Drop to upload
            </p>
            <p
              className="text-[10px] mt-1"
              style={{ color: "var(--t3)", fontFamily: "var(--font-mono)" }}
            >
              files or folders
            </p>
          </div>
        </div>
      )}

      {uploadProgress && (
        <UploadProgressBar progress={uploadProgress} />
      )}
    </div>
  );
}

function UploadProgressBar({
  progress,
}: {
  progress: {
    done: number;
    total: number;
    failed: number;
    bytesDone: number;
    bytesTotal: number;
    currentName: string | null;
  };
}) {
  const isDone = progress.done >= progress.total;
  // Prefer bytes for the bar (more honest when a single file dwarfs the
  // others); fall back to file count if every file is somehow zero bytes.
  const pct =
    progress.bytesTotal > 0
      ? (progress.bytesDone / progress.bytesTotal) * 100
      : (progress.done / Math.max(progress.total, 1)) * 100;
  const accent =
    progress.failed > 0 && isDone
      ? "var(--err)"
      : isDone
        ? "var(--ok)"
        : "var(--accent)";

  return (
    <div
      className="border-t"
      style={{
        borderColor: "var(--blt)",
        background: "var(--surface)",
        flexShrink: 0,
      }}
    >
      {/* Status line */}
      <div
        className="px-2 pt-1.5 pb-1 flex items-center gap-1.5 text-[10px]"
        style={{ color: "var(--t2)", fontFamily: "var(--font-mono)" }}
      >
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{
            background: accent,
            animation: !isDone ? "pulse 1.2s ease-in-out infinite" : undefined,
          }}
        />
        <span className="tabular-nums shrink-0">
          {isDone
            ? progress.failed > 0
              ? `Done — ${progress.failed} failed`
              : "Uploaded"
            : `${progress.done} / ${progress.total}`}
        </span>
        {!isDone && progress.currentName && (
          <span
            className="truncate flex-1 min-w-0"
            style={{ color: "var(--t3)" }}
            title={progress.currentName}
          >
            {progress.currentName}
          </span>
        )}
        <span
          className="tabular-nums shrink-0 ml-auto"
          style={{ color: "var(--t3)" }}
        >
          {progress.bytesTotal > 0
            ? `${formatBytes(progress.bytesDone)} / ${formatBytes(progress.bytesTotal)}`
            : `${Math.round(pct)}%`}
        </span>
      </div>
      {/* Progress bar */}
      <div
        className="h-[2px] w-full"
        style={{ background: "var(--blt)" }}
      >
        <div
          className="h-full transition-all duration-200 ease-out"
          style={{
            width: `${Math.min(100, Math.max(2, pct))}%`,
            background: accent,
          }}
        />
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function FileRow({
  entry,
  deskId,
  depth,
  onFileClick,
  onDelete,
  autoRefresh,
  selectedPath,
  relayUrl,
  parentPath,
  browseRoot,
}: {
  entry: FileEntry;
  deskId: string;
  depth: number;
  onFileClick: (path: string, name: string, root: "data" | "sprite") => void;
  onDelete: (path: string) => void;
  autoRefresh: boolean;
  selectedPath: string | null;
  relayUrl: string | null;
  parentPath: string;
  browseRoot: "data" | "sprite";
}) {
  const [expanded, setExpanded] = useState(false);
  const { data: children } = useFiles(
    deskId,
    entry.path,
    expanded && autoRefresh,
    browseRoot,
  );
  const childEntries = expanded ? (children?.entries ?? []) : [];

  const ext = entry.name.split(".").pop()?.toLowerCase() ?? "";
  const isImage = IMAGE_EXTS.has(ext);
  const isSelected = !entry.isDir && selectedPath === entry.path;

  const fullPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
  const isAppsRoot = entry.name === "apps" && depth === 0 && entry.isDir;
  const isInApps = parentPath === "apps" || parentPath.startsWith("apps/");
  const showOpenLink = !!relayUrl && (isAppsRoot || isInApps);

  // App URL composition.
  //
  // Files inside `apps/<name>/...` resolve through the orchestrator's
  // `/api/apps/:deskId/:appName/*` proxy, NOT the raw sprite URL. The
  // orchestrator wakes the sprite if it's cold (using exec-based wake,
  // which is more reliable than Fly's HTTP-edge auto-wake — see
  // packages/orchestrator/src/index.ts for the routing logic). Net
  // effect: a shared link to a published app keeps working even when
  // the sprite has been idle for hours; pre-fix the user would hit a
  // 502 from the Sprites edge proxy when the cold-start exceeded its
  // timeout.
  //
  // The apps/ ROOT listing still uses the raw relay URL because it's a
  // niche case (you usually share a specific app, not the index) and
  // adding a parallel listing endpoint to the orchestrator wasn't worth
  // the surface area today.
  const appUrl = () => {
    if (!relayUrl) return "#";
    if (isAppsRoot) return `${relayUrl}/apps/`;
    // fullPath looks like "apps/<appName>/<rest>...". Split off the
    // first two segments and route the remainder through the orchestrator.
    const parts = fullPath.split("/");
    if (parts[0] !== "apps" || parts.length < 2) {
      return `${relayUrl}/${fullPath}${entry.isDir ? "/" : ""}`;
    }
    const appName = parts[1];
    const rest = parts.slice(2).join("/");
    const trailingSlash = entry.isDir && rest !== "" ? "/" : "";
    const restPath = rest ? `/${rest}${trailingSlash}` : "/";
    return `${window.location.origin}/api/apps/${deskId}/${appName}${restPath}`;
  };

  // Files inside apps/<name>/... are deliverables — clicking them should
  // show the rendered/hosted version (the whole point of publishing them),
  // not the raw bytes. The orchestrator's /api/apps proxy wakes the sprite
  // on demand, so the link works even when the desk is asleep.
  // Source can still be inspected via the Eye button on hover.
  const isHostableFile = isInApps && !entry.isDir;
  const handleClick = () => {
    if (entry.isDir) {
      setExpanded(!expanded);
    } else if (isHostableFile) {
      window.open(appUrl(), "_blank", "noopener,noreferrer");
    } else {
      onFileClick(entry.path, entry.name, browseRoot);
    }
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: row contains nested interactive children (delete button, expand toggle); a real <button> can't wrap them. */}
      <div
        className="w-full flex items-center gap-1.5 px-1.5 py-[5px] rounded text-left group text-[12.5px] cursor-pointer"
        style={{
          paddingLeft: `${depth * 12 + 4}px`,
          background: isSelected
            ? "color-mix(in srgb, var(--accent) 12%, transparent)"
            : "transparent",
          color: isSelected ? "var(--accent)" : "var(--t2)",
          fontFamily: isSelected ? "var(--font-mono)" : "var(--font-body)",
        }}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        onDragStart={(e) => {
          if (!entry.isDir) {
            e.dataTransfer.setData("text/rondel-file-path", entry.path);
            e.dataTransfer.effectAllowed = "copy";
          }
        }}
        draggable={!entry.isDir}
        onMouseEnter={(e) => {
          if (!isSelected) {
            (e.currentTarget as HTMLElement).style.background = "var(--hover)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            (e.currentTarget as HTMLElement).style.background = "transparent";
          }
        }}
        role="button"
        tabIndex={0}
      >
        {entry.isDir ? (
          <>
            {expanded ? (
              <ChevronDown
                size={11}
                className="shrink-0"
                style={{ color: "var(--t4)" }}
              />
            ) : (
              <ChevronRight
                size={11}
                className="shrink-0"
                style={{ color: "var(--t4)" }}
              />
            )}
            {expanded ? (
              <FolderOpen
                size={13}
                className="shrink-0"
                style={{ color: "var(--accent)" }}
              />
            ) : (
              <Folder
                size={13}
                className="shrink-0"
                style={{ color: "var(--accent)" }}
              />
            )}
          </>
        ) : (
          <>
            <span className="w-[11px] shrink-0" />
            {isImage ? (
              <ImageIcon
                size={12}
                className="shrink-0"
                style={{ color: "var(--info)" }}
              />
            ) : ext === "md" || ext === "txt" || ext === "log" ? (
              <FileText
                size={12}
                className="shrink-0"
                style={{ color: "var(--t3)" }}
              />
            ) : (
              <FileCode
                size={12}
                className="shrink-0"
                style={{ color: "var(--t3)" }}
              />
            )}
          </>
        )}
        <span
          className="truncate flex-1"
          style={
            isHostableFile
              ? {
                  color: isSelected ? "var(--accent)" : "var(--t1)",
                }
              : undefined
          }
        >
          {entry.name}
        </span>
        {/*
          Persistent external-link tag for hostable files (so the row
          obviously opens externally on click). For folders inside apps,
          the icon is hover-only — folders still expand on click, the icon
          is the explicit "open hosted" affordance.
        */}
        {isHostableFile && (
          <ExternalLink
            size={9}
            className="shrink-0"
            style={{ color: "var(--accent)", opacity: 0.7 }}
          />
        )}
        {!entry.isDir && (
          <span
            className="text-[10px] shrink-0 opacity-0 group-hover:opacity-100 tabular-nums"
            style={{ color: "var(--t4)", fontFamily: "var(--font-mono)" }}
          >
            {formatSize(entry.size)}
          </span>
        )}
        {showOpenLink && entry.isDir && (
          <a
            href={appUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="p-0.5 rounded shrink-0 opacity-0 group-hover:opacity-100"
            style={{ color: "var(--accent)" }}
            title="Open hosted version"
          >
            <ExternalLink size={9} />
          </a>
        )}
        {isHostableFile && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onFileClick(entry.path, entry.name, browseRoot);
            }}
            className="p-0.5 rounded shrink-0 opacity-0 group-hover:opacity-100"
            style={{ color: "var(--t3)" }}
            title="View source"
          >
            <Eye size={9} />
          </button>
        )}
        {entry.isDir && (
          <a
            href={api.downloadFolderUrl(deskId, entry.path, browseRoot)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="p-0.5 rounded shrink-0 opacity-0 group-hover:opacity-100"
            style={{ color: "var(--t3)" }}
            title="Download as ZIP"
          >
            <Download size={9} />
          </a>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm(`Delete ${entry.name}?`)) onDelete(entry.path);
          }}
          className="p-0.5 rounded shrink-0 opacity-0 group-hover:opacity-100"
          style={{ color: "var(--t4)" }}
          title="Delete"
        >
          <Trash2 size={9} />
        </button>
      </div>
      {expanded &&
        childEntries
          .slice()
          .sort((a, b) => {
            if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
            return a.name.localeCompare(b.name);
          })
          .map((child) => (
            <FileRow
              key={child.name}
              entry={child}
              deskId={deskId}
              depth={depth + 1}
              onFileClick={onFileClick}
              onDelete={onDelete}
              autoRefresh={autoRefresh}
              selectedPath={selectedPath}
              relayUrl={relayUrl}
              parentPath={fullPath}
              browseRoot={browseRoot}
            />
          ))}
    </>
  );
}

function FilesSkeleton() {
  return (
    <div className="px-1 py-1 space-y-1.5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-4 rounded animate-pulse"
          style={{
            background: "var(--border)",
            opacity: 1 - i * 0.12,
            width: `${55 + ((i * 7) % 30)}%`,
            marginLeft: `${(i % 3) * 8}px`,
          }}
        />
      ))}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}K`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}M`;
}
